import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Plus, X, Award } from 'lucide-react';

const HonorsForm = ({ data, onSave }) => {
  const [honors, setHonors] = useState(data && data.length > 0 ? data : []);

  const levels = ['School', 'Regional', 'State', 'National', 'International'];
  const grades = ['9', '10', '11', '12', 'Post-Graduate'];

  const addHonor = () => {
    if (honors.length < 5) {
      setHonors([...honors, {
        title: '',
        level: 'School',
        grade_received: '11',
        description: ''
      }]);
    }
  };

  const removeHonor = (index) => {
    setHonors(honors.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updated = [...honors];
    updated[index][field] = value;
    setHonors(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(honors);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Tip:</strong> List up to 5 academic or extracurricular honors/awards you've received.
        </p>
      </div>

      {honors.length === 0 && (
        <Card className="p-8 text-center">
          <Award className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No honors added yet</p>
          <Button type="button" onClick={addHonor}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Honor
          </Button>
        </Card>
      )}

      {honors.map((honor, index) => (
        <Card key={index} className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Honor/Award {index + 1}</h3>
            <Button type="button" variant="ghost" size="sm" onClick={() => removeHonor(index)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={honor.title}
                onChange={(e) => handleChange(index, 'title', e.target.value)}
                placeholder="e.g., National Merit Scholar"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Level *</Label>
                <Select value={honor.level} onValueChange={(value) => handleChange(index, 'level', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {levels.map(level => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grade Received *</Label>
                <Select value={honor.grade_received} onValueChange={(value) => handleChange(index, 'grade_received', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {grades.map(grade => (
                      <SelectItem key={grade} value={grade}>Grade {grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description (100 characters max)</Label>
              <Textarea
                value={honor.description}
                onChange={(e) => handleChange(index, 'description', e.target.value.substring(0, 100))}
                rows={2}
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">{honor.description?.length || 0}/100</p>
            </div>
          </div>
        </Card>
      ))}

      {honors.length > 0 && honors.length < 5 && (
        <Button type="button" variant="outline" onClick={addHonor} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Another Honor ({honors.length}/5)
        </Button>
      )}

      <div className="flex justify-end">
        <Button type="submit">Save & Continue</Button>
      </div>
    </form>
  );
};

export default HonorsForm;